/**
 * Pieces the Security, Contacts, Transfers and lifecycle screens share: the step-up wrapper that
 * knows when the person backs out, the account query, and the "Security status" rows.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useRef, useState, type ReactNode } from "react";

import { ConfirmIdentityModal } from "@/components/domains/shared";
import { identity } from "@/lib/api";
import {
  verificationApplies,
  type DomainDetail,
  type Operation,
  type OperationKind,
  type PillTone,
} from "@/lib/domains";
import { ApiError } from "@/lib/http";
import { Badge, KeyValue } from "@/components/ui";
import { RuleRow } from "@/components/domains/parts";

/** Thrown when the person closes "Confirm it's you" instead of signing in again. */
export class StepUpCancelled extends Error {
  constructor() {
    super("Cancelled");
  }
}

/** Hides the cancellation (it isn't an error to show). */
export const shownError = (error: unknown): unknown => (error instanceof StepUpCancelled ? null : error);

export interface StepUpCopy {
  subtitle?: ReactNode;
  label?: string;
  hint?: ReactNode;
}

/**
 * Like `useStepUp` from the shared kit, but the modal's wording can follow the action (Figma
 * "Confirm it's you" for the authorization code) and closing it rejects with `StepUpCancelled`, so
 * a mutation waiting on it settles instead of spinning forever.
 */
export function useConfirmed() {
  const [copy, setCopy] = useState<StepUpCopy | null>(null);
  const waiter = useRef<null | { retry: () => void; cancel: () => void }>(null);

  const run = useCallback(<T,>(action: () => Promise<T>, wording: StepUpCopy = {}): Promise<T> => {
    return action().catch((err: unknown) => {
      if (!(err instanceof ApiError && err.reason === "REAUTHENTICATION_REQUIRED")) throw err;
      return new Promise<T>((resolve, reject) => {
        waiter.current = {
          retry: () => {
            action().then(resolve, reject);
          },
          cancel: () => reject(new StepUpCancelled()),
        };
        setCopy(wording);
      });
    });
  }, []);

  const modal = (
    <ConfirmIdentityModal
      open={copy !== null}
      subtitle={copy?.subtitle}
      confirmLabel={copy?.label}
      onClose={() => {
        setCopy(null);
        waiter.current?.cancel();
        waiter.current = null;
      }}
      onConfirmed={() => {
        setCopy(null);
        waiter.current?.retry();
        waiter.current = null;
      }}
    >
      {copy?.hint}
    </ConfirmIdentityModal>
  );
  return { run, modal };
}

/** The signed-in account (for "2FA on account"). */
export function useAccount() {
  return useQuery({ queryKey: ["me"], queryFn: identity.me });
}

export function pendingOp(detail: DomainDetail, ...kinds: OperationKind[]): Operation | undefined {
  return detail.operations.find((o) => o.status === "pending" && kinds.includes(o.kind));
}

export function dnssecPill(state: DomainDetail["domain"]["dnssec"]): { label: string; tone: PillTone } {
  switch (state) {
    case "on":
      return { label: "Enabled", tone: "success" };
    case "enabling":
      return { label: "Enabling", tone: "warning" };
    case "disabling":
      return { label: "Turning off", tone: "warning" };
    default:
      return { label: "Not configured", tone: "neutral" };
  }
}

/** Figma "Security status": transfer lock, DNSSEC, 2FA on the account, registrant verified. */
export function SecurityStatusRows({ detail, divided }: { detail: DomainDetail; divided?: boolean }) {
  const d = detail.domain;
  const me = useAccount();
  const lockPending = Boolean(pendingOp(detail, "transfer_lock"));
  const dnssec = dnssecPill(d.dnssec);
  const verification = detail.contact?.verification;
  const required = verificationApplies(detail.contact, d.fqdn);
  const rows: { label: string; value: ReactNode }[] = [
    {
      label: "Transfer lock",
      value: (
        <Badge size="md" tone={lockPending ? "warning" : d.transfer_lock ? "success" : "danger"}>
          {lockPending ? "Updating" : d.transfer_lock ? "On" : "Off"}
        </Badge>
      ),
    },
    {
      label: "DNSSEC",
      value: (
        <Badge size="md" tone={dnssec.tone}>
          {dnssec.label}
        </Badge>
      ),
    },
    {
      label: "2FA on account",
      value: me.data ? (
        <Badge size="md" tone={me.data.mfa_enabled ? "success" : "warning"}>
          {me.data.mfa_enabled ? "On" : "Off"}
        </Badge>
      ) : (
        <Badge size="md" tone="neutral">
          —
        </Badge>
      ),
    },
    {
      label: "Registrant verified",
      value: !verification ? (
        <Badge size="md" tone="neutral">
          No registrant
        </Badge>
      ) : !required ? (
        <Badge size="md" tone="neutral">
          Not required
        </Badge>
      ) : (
        <Badge size="md" tone={verification === "verified" ? "success" : "warning"}>
          {verification === "verified" ? "Yes" : "Pending"}
        </Badge>
      ),
    },
  ];
  return (
    <>
      {rows.map((r, i) =>
        divided ? (
          <RuleRow key={r.label} first={i === 0}>
            <p className="min-w-0 flex-1 text-sm font-medium text-[#5c5551]">{r.label}</p>
            {r.value}
          </RuleRow>
        ) : (
          <KeyValue key={r.label} label={r.label}>
            {r.value}
          </KeyValue>
        ),
      )}
    </>
  );
}
